import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import { GeneratorShell } from '../components/GeneratorShell';
import {
  generateNanoIds,
  generateUlids,
  NANOID_PRESETS,
} from '../lib/shortId';

type Tab = 'nanoid' | 'ulid';

const COUNT_PRESETS = [1, 5, 10, 50, 100, 500, 1000];

export default function NanoIdTool(): JSX.Element {
  const [tab, setTab] = useState<Tab>('nanoid');
  const [length, setLength] = useState<number>(21);
  const [presetId, setPresetId] = useState<string>('urlsafe');
  const [customAlphabet, setCustomAlphabet] = useState<string>('');
  const [count, setCount] = useState<number>(10);
  const [monotonic, setMonotonic] = useState<boolean>(true);
  const [tick, setTick] = useState(0);

  const useCustomAlphabet = presetId === 'custom';
  const alphabet = useCustomAlphabet
    ? customAlphabet
    : (NANOID_PRESETS.find((p) => p.id === presetId)?.alphabet ?? undefined);

  const { items, error } = useMemo<{ items: string[]; error?: string }>(() => {
    try {
      if (tab === 'nanoid') {
        // The default URL-safe set is signalled by passing
        // `alphabet: undefined` to skip customAlphabet.
        const list = generateNanoIds({
          length,
          count,
          alphabet: presetId === 'urlsafe' ? undefined : alphabet,
        });
        return { items: list };
      }
      return { items: generateUlids({ count, monotonic }) };
    } catch (e) {
      return { items: [], error: e instanceof Error ? e.message : String(e) };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, length, alphabet, presetId, count, monotonic, tick]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="nanoid"
        category="generate"
        description="NanoID 短随机 ID（默认 21 字符 URL-safe）+ ULID 时间有序 ID（默认单调递增）。"
      />

      {/* Tab strip */}
      <div className="flex items-center gap-1">
        <TabButton active={tab === 'nanoid'} onClick={() => setTab('nanoid')}>
          NanoID
        </TabButton>
        <TabButton active={tab === 'ulid'} onClick={() => setTab('ulid')}>
          ULID
        </TabButton>
      </div>

      <GeneratorShell
        controls={
          tab === 'nanoid' ? (
            <NanoIdControls
              length={length}
              onLengthChange={setLength}
              presetId={presetId}
              onPresetIdChange={setPresetId}
              customAlphabet={customAlphabet}
              onCustomAlphabetChange={setCustomAlphabet}
              count={count}
              onCountChange={setCount}
            />
          ) : (
            <UlidControls
              count={count}
              onCountChange={setCount}
              monotonic={monotonic}
              onMonotonicChange={setMonotonic}
            />
          )
        }
        items={items}
        error={error}
        onRegenerate={() => setTick((t) => t + 1)}
      />
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'rounded-md border px-3 py-1 text-[12px] font-medium transition',
        active
          ? 'border-accent bg-accent/10 text-accent'
          : 'border-line text-ink-muted hover:border-accent/60 hover:text-accent',
      )}
    >
      {children}
    </button>
  );
}

interface NanoIdControlsProps {
  length: number;
  onLengthChange: (n: number) => void;
  presetId: string;
  onPresetIdChange: (id: string) => void;
  customAlphabet: string;
  onCustomAlphabetChange: (s: string) => void;
  count: number;
  onCountChange: (n: number) => void;
}

function NanoIdControls(p: NanoIdControlsProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[11px] text-ink-muted">长度</span>
      <input
        type="number"
        min={4}
        max={64}
        value={p.length}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) p.onLengthChange(n);
        }}
        className="input w-16 py-0.5 text-[11.5px]"
      />
      <span className="text-[11px] text-ink-muted">字符集</span>
      <select
        className="input py-0.5 text-[11.5px]"
        value={p.presetId}
        onChange={(e) => p.onPresetIdChange(e.target.value)}
      >
        {NANOID_PRESETS.map((preset) => (
          <option key={preset.id} value={preset.id}>
            {preset.label}
          </option>
        ))}
        <option value="custom">自定义</option>
      </select>
      {p.presetId === 'custom' && (
        <input
          type="text"
          className="input w-[240px] py-0.5 font-mono text-[11px]"
          placeholder="≥2 个不同字符，例: 0123456789abcdef"
          value={p.customAlphabet}
          onChange={(e) => p.onCustomAlphabetChange(e.target.value)}
          spellCheck={false}
        />
      )}
      <span className="text-[11px] text-ink-muted">数量</span>
      <select
        className="input py-0.5 text-[11.5px]"
        value={p.count}
        onChange={(e) => p.onCountChange(Number(e.target.value))}
      >
        {COUNT_PRESETS.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    </div>
  );
}

interface UlidControlsProps {
  count: number;
  onCountChange: (n: number) => void;
  monotonic: boolean;
  onMonotonicChange: (v: boolean) => void;
}

function UlidControls(p: UlidControlsProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[11px] text-ink-muted">数量</span>
      <select
        className="input py-0.5 text-[11.5px]"
        value={p.count}
        onChange={(e) => p.onCountChange(Number(e.target.value))}
      >
        {COUNT_PRESETS.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-line px-2 py-0.5 text-[11.5px] text-ink-muted hover:border-accent/60">
        <input
          type="checkbox"
          checked={p.monotonic}
          onChange={(e) => p.onMonotonicChange(e.target.checked)}
        />
        单调递增（同毫秒内严格有序）
      </label>
    </div>
  );
}
